/* ═══════════════════════════════════════════════════════════════
   FORMAT — one way to write a date, a time and a distance.

   UX audit 2026-10-16 (B5): the screens used five date formats
   ('Fri 16 Oct', '14 Oct 26', '2026-10-14 14:22:00', '16/10/2026',
   'Sept' beside 'Sep'). These are the house forms:

     fmtDay       'Wed 14 Oct'          lists and rows
     fmtShortDate '14 Oct'              tight columns
     fmtDate      '14 Oct 2026'         documents, anything archival
     fmtTime      '14:22'               24-hour, always
     fmtDateTime  'Wed 14 Oct, 14:22'
     fmtKm        '1,109 km'

   The year is added to the list forms only when it is not this year.
   Month names come from a fixed table: en-GB's toLocaleDateString writes
   'Sept', which is how the two spellings got in. Pure, never throws;
   '' on anything unparseable.
   ═══════════════════════════════════════════════════════════════ */

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const DAY_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;

/* A day-only string is a calendar day in the operator's own zone, never
   UTC midnight (which reads as the previous day west of Greenwich). */
export function toDate(value) {
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value === 'number') {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  if (typeof value !== 'string' || !value.trim()) return null;
  const s = value.trim();
  const m = DAY_ONLY.exec(s);
  if (m) {
    const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    return d.getMonth() === Number(m[2]) - 1 ? d : null;
  }
  // 'YYYY-MM-DD HH:MM[:SS]' is not ISO for every engine; make it so.
  const d = new Date(/^\d{4}-\d{2}-\d{2} \d/.test(s) ? s.replace(' ', 'T') : s);
  return Number.isNaN(d.getTime()) ? null : d;
}

const hasTime = (value) => typeof value !== 'string' || /\d[T ]\d{1,2}:\d{2}/.test(value);
const yearSuffix = (d, now) => (d.getFullYear() === now.getFullYear() ? '' : ` ${d.getFullYear()}`);

/** 'Wed 14 Oct' (year added when not this year). */
export function fmtDay(value, { now = new Date() } = {}) {
  const d = toDate(value);
  return d ? `${DAYS[d.getDay()]} ${d.getDate()} ${MONTHS[d.getMonth()]}${yearSuffix(d, now)}` : '';
}

/** '14 Oct' (year added when not this year). */
export function fmtShortDate(value, { now = new Date() } = {}) {
  const d = toDate(value);
  return d ? `${d.getDate()} ${MONTHS[d.getMonth()]}${yearSuffix(d, now)}` : '';
}

/** '14 Oct 2026'. */
export function fmtDate(value) {
  const d = toDate(value);
  return d ? `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}` : '';
}

/** '14:22'. '' when the value carries no time of day. */
export function fmtTime(value) {
  if (!hasTime(value)) return '';
  const d = toDate(value);
  if (!d) return '';
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

/** 'Wed 14 Oct, 14:22' — the time dropped when there is none. */
export function fmtDateTime(value, opts) {
  const day = fmtDay(value, opts);
  const time = fmtTime(value);
  return day && time ? `${day}, ${time}` : day;
}

/** 1109.4 → '1,109 km'. '' on rubbish. */
export function fmtKm(km) {
  const n = Number(km);
  if (km === null || km === undefined || km === '' || !Number.isFinite(n)) return '';
  return `${Math.round(n).toLocaleString('en-GB')} km`;
}
